import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import FormControl from "@mui/material/FormControl";
import {
    Text,
    TextInput,
    PhoneInput,
    DateInput,
    Services,
    Modal,
    i18n,
    tokens,
    localizationMaps,
    BubbleSelectBlock
} from '@simplybenefits/sb-ui-library';
import * as notifier from '../../../actions/notifier.js';
import { mergeAndParseUserData, returnChangeRequestChanges, returnRegularInfoChanges } from './parseUserData.js';

class Settings extends Component {
    state = {
        firstname: '',
        lastname: '',
        email: '',
        addressLineTwo: '',
        addressLineOne: '',
        postalCode: '',
        city: '',
        province: '',
        country: 'Canada',
        phone: '',
        birthdate: '',
        maritlStat: '',
        gender: '',
        coverageType: '',
        language: ''
    };

    async componentDidMount() {
        const { firstname, lastname, email, coverageType, maritlStat, gender, phone, birthdate, addressLineTwo, addressLineOne, city, postalCode, province, useMFA } = this.props.meta;

        await this.setState({
            firstname: firstname || '',
            lastname: lastname || '',
            email: email || '',
            addressLineOne: addressLineOne || '',
            addressLineTwo: addressLineTwo || '',
            city: city || '',
            postalCode: postalCode || '',
            province: localizationMaps.fromEnglish("provinces", province) || '',
            phone: phone || '',
            birthdate: Services.dates.formatIncomingDate(birthdate) || '',
            maritlStat: localizationMaps.fromEnglish("maritalStatuses", maritlStat) || '',
            gender: localizationMaps.fromEnglish("genders", gender) || '',
            coverageType: localizationMaps.fromEnglish("coverageTypes", coverageType) || '',
            language: this.props.meta.language === "fr" ? "Français" : "English",
            useMFA: Boolean(useMFA) || false
        });
    }

    componentDidUpdate(prevProps) {
        if (prevProps.meta.language !== this.props.meta.language) {
            this.setState({ language: this.props.meta.language === "fr" ? "Français" : "English" });
        }
    }

    _onBlur = async (type) => {
        this.setState({
            [`${ type }Error`]: await Services.validateInput(type, this.state[type])
        });
    };

    _handleInputChange = (type, event, isValue = false) => {
        this.setState({
            [type]: isValue ? event : event.target.value.toString()
        });
    };

    _validateForm = async () => {
        const phoneError = await Services.validateInput('phone', this.state.phone);
        const addressLineOneError = await Services.validateInput('addressLineOne', this.state.addressLineOne);
        const cityError = await Services.validateInput('city', this.state.city);
        const provinceError = await Services.validateInput('province', this.state.province);
        const postalCodeError = await Services.validateInput('postalCode', this.state.postalCode);
        const maritlStatError = await Services.validateInput('maritlStat', this.state.maritlStat);
        const genderError = await Services.validateInput('gender', this.state.gender);
        const coverageTypeError = await Services.validateInput('coverageType', this.state.coverageType);

        this.setState({
            phoneError,
            addressLineOneError,
            cityError,
            provinceError,
            postalCodeError,
            maritlStatError,
            genderError,
            coverageTypeError
        });

        if (
            phoneError ||
            provinceError ||
            addressLineOneError ||
            cityError ||
            provinceError ||
            postalCodeError ||
            maritlStatError ||
            genderError ||
            coverageTypeError
        ) {
            this.props.dispatch(notifier.displayError({ verbiage: i18n.t(tokens.labels.pleaseConfirmDetailsError) }));

            return false;
        }

        this._confirm();

        return true;
    };

    _confirm = () => {
        const changeRequestChanges = returnChangeRequestChanges(this.state, this.props.meta);
        const regularDataChanges = returnRegularInfoChanges(this.state, this.props.meta);
        let userDataForUpdate = false;
        
        if (regularDataChanges) {
            userDataForUpdate = mergeAndParseUserData(this.props.meta, regularDataChanges);
        }
 
        // this will conditionally perform a user settings update, a change request creation or both, depending on which data fields have changed
        this.props.updateUserSettingsConditionally(changeRequestChanges, userDataForUpdate);
        return true;
    };

    /**
     * 
     * @param {*} input 
     */
    _formatPostalCode = (input) => {
        const formattedPostalCode = input
            .toUpperCase()
            .trim()
            .replace(/\s+/g, '') // Remove all spaces
            .replace(/([A-Z0-9]{3})([A-Z0-9]{3})/, '$1 $2'); // Add a space after the first 3 characters
        
        this.setState({ postalCode: formattedPostalCode });
    }; 

    render() {
        const {
            birthdate,
            coverageType,
            email,
            firstname,
            lastname,

            addressLineTwo,
            addressLineOne,
            city,
            province,
            postalCode,

            phone,
            gender,
            maritlStat,
            language,
            useMFA
        } = this.state;

        const styles = {
            form: {
                display: 'flex',
                flexDirection: 'column'
            },
            row: {
                display: 'flex',
                flexDirection: 'row'
            },
            divider: {
                width: 40
            }
        };

        return (
            <Modal
                testid="settings-modal"
                open={true}
                medium
                onClose={this.props.close}
                title={ i18n.t(tokens.titles.accountDetails)}
                primaryButton={{
                    onClick: this._validateForm
                }}
            >
                <Text medium bold>
                    { i18n.t(tokens.labels.changeRequests.personalDetails) }
                </Text>
                
                <div style={styles.row}>
                    <TextInput
                        label={ i18n.t(tokens.labels.firstname) }
                        value={firstname}
                        onBlur={() => this._onBlur('firstname')}
                        onChange={(value) => this._handleInputChange('firstname', value)}
                        fullWidth
                    />

                    <div style={styles.divider} />

                    <TextInput
                        label={ i18n.t(tokens.labels.lastname) }
                        value={lastname}
                        onBlur={() => this._onBlur('lastname')}
                        onChange={(value) => this._handleInputChange('lastname', value)}
                        fullWidth
                    />
                </div>
                
                <div style={styles.row}>
                    <TextInput
                        label={ i18n.t(tokens.labels.email) }
                        value={email}
                        onBlur={() => this._onBlur('email')}
                        onChange={(value) => this._handleInputChange('email', value)}
                        fullWidth
                    />
                    <div style={styles.divider} />
                    <DateInput
                        label={ i18n.t(tokens.labels.account.dateOfBirthYYYYMMDD)}
                        value={birthdate}
                        onBlur={() => this._onBlur('birthdate')}
                        onChange={(value) => this._handleInputChange('birthdate', value)}
                        fullWidth
                    />
                </div>

                <div style={styles.row}>
                    <PhoneInput
                        label={ i18n.t(tokens.labels.phoneNumber) }
                        value={phone}
                        onBlur={() => this._onBlur('phone')}
                        onChange={(value) => this._handleInputChange('phone', value)}
                        error={Boolean(this.state.phoneError)}
                        helperText={this.state.phoneError}
                        fullWidth
                    />
                    <div style={styles.divider} />
                    <TextInput
                        label={ i18n.t(tokens.labels.maritalStatus) }
                        fullWidth
                        required
                        value={ maritlStat }
                        onBlur={() => this._onBlur('maritlStat')}
                        onChange={(value) => this._handleInputChange('maritlStat', value)}
                        select
                        selectOptions={ Services.selectFieldOptions.maritalStatusOptions()}
                    />
                </div>

                <div style={styles.row}>
                    <TextInput
                        label={ i18n.t(tokens.labels.gender) }
                        fullWidth
                        required
                        value={ gender }
                        onBlur={() => this._onBlur('gender')}
                        onChange={(value) => this._handleInputChange('gender', value)}
                        error={Boolean(this.state.genderError)}
                        helperText={this.state.genderError}
                        select
                        selectOptions={ Services.selectFieldOptions.genderOptions() }
                    />
                    <div style={styles.divider} />
                    <TextInput
                        label={ i18n.t(tokens.labels.coverageType) }
                        value={coverageType}
                        disabled
                        onBlur={() => this._onBlur('coverageType')}
                        onChange={(value) => this._handleInputChange('coverageType', value)}
                        error={Boolean(this.state.coverageTypeError)}
                        helperText={this.state.coverageTypeError}
                        select
                        selectOptions={ Services.selectFieldOptions.coverageOptions() }
                        fullWidth
                    />
                </div>

                <div style={styles.row}>
                    <TextInput
                        label={ i18n.t(tokens.labels.signup.language) }
                        value={ language }
                        onBlur={() => this._onBlur('language')}
                        onChange={(value) => this._handleInputChange('language', value)}
                        select
                        selectOptions={ Services.selectFieldOptions.languageOptionsNotTranslated() }
                        fullWidth
                    />

                    {/* placeholders */}
                    <div style={styles.divider} />
                    <div style={{ width: '100%' }}/>
                </div>

                <Text medium bold customStyle={{ marginTop: 10 }}>
                    { i18n.t(tokens.labels.account.addressInformation) }
                </Text>

                <TextInput
                    label={ i18n.t(tokens.labels.addressLineOne) }
                    autocomplete="shipping street-address"
                    value={addressLineOne}
                    onBlur={() => this._onBlur('addressLineOne')}
                    onChange={(value) => this._handleInputChange('addressLineOne', value)}
                    error={Boolean(this.state.addressLineOneError)}
                    helperText={this.state.addressLineOneError}
                    fullWidth
                    required
                /> 

                <div style={styles.row}>
                    <TextInput
                        label={ i18n.t(tokens.labels.addressLineTwo)}
                        value={addressLineTwo}
                        onBlur={() => this._onBlur('addressLineTwo')}
                        onChange={(value) => this._handleInputChange('addressLineTwo', value)}
                        fullWidth
                        helperText={i18n.t(tokens.labels.addressLineTwoExample)}
                    />

                    <div style={styles.divider} />

                    <TextInput
                        label={ i18n.t(tokens.labels.account.cityTown)}
                        autocomplete="shipping locality"
                        value={city}
                        onBlur={() => this._onBlur('city')}
                        onChange={(value) => this._handleInputChange('city', value)}
                        error={Boolean(this.state.cityError)}
                        helperText={this.state.cityError}
                        fullWidth
                        required
                    />
                </div>

                <div style={styles.row}>
                    <TextInput
                        label={ i18n.t(tokens.labels.province)}
                        autocomplete="shipping region"
                        value={ province }
                        onBlur={() => this._onBlur('province')}
                        onChange={(value) => this._handleInputChange('province', value)}
                        select
                        selectOptions={Services.selectFieldOptions.provinceOptions()}
                        fullWidth
                        required
                    />

                    <div style={styles.divider} />

                    <TextInput
                        label={ i18n.t(tokens.labels.postalCode) }
                        autocomplete="shipping postal-code"
                        value={postalCode}
                        onBlur={(e) => this._formatPostalCode(e.target.value)}
                        onChange={(e) => {
                            // Allow typing only 6 alphanumeric characters and trim any excess
                            const postalCodeValue = e.target.value.toUpperCase().slice(0, 7);
                            this.setState({ postalCode: postalCodeValue });
                        }}
                        error={Boolean(this.state.postalCodeError)}
                        helperText={this.state.postalCodeError}
                        fullWidth
                        required
                    />
                </div>

                <FormControl component="fieldset">
                    <Text
                        medium
                        light
                        margin="20px 0 0px 0"
                    >
                        { i18n.t(tokens.labels.account.enforceMFA) }
                    </Text>

                    <Text
                        tiny
                        light
                        margin="5px 0 5px 0"
                    >
                        { i18n.t(tokens.labels.account.enforceMFAMessage) }
                    </Text>

                    <BubbleSelectBlock
                        disabled={ this.props.meta.enforceMFA }
                        first={{
                            title: i18n.t(tokens.labels.yes),
                            selected: this.props.meta.enforceMFA || Boolean(this.state.useMFA) === true,
                            field: 'enforceMFAYes'
                        }}
                        second={{
                            title: i18n.t(tokens.labels.no),
                            selected: !this.props.meta.enforceMFA && Boolean(this.state.useMFA) === false,
                            field: 'enforceMFANo'
                        }}
                        onBlur={(e) => this._onBlur('useMFA', e)}
                        handleChange={ async (field) => {
                            if (field === 'enforceMFAYes') {
                                await this._handleInputChange('useMFA', true, true);
                            } else {
                                await this._handleInputChange('useMFA', false, true);
                            }
                        }}
                    />

                    {
                        Boolean(this.props.meta.enforceMFA) &&
                            <Text
                                tiny
                                light
                                margin="5px 0 5px 0"
                            >
                                { i18n.t(tokens.labels.account.mfaEnforcedByAccount) }
                            </Text>
                    }
                </FormControl>
            </Modal>
        );
    }
}

Settings.propTypes = {
    meta: PropTypes.object
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    whitelabel: state.whitelabel,
    meta: state.user.meta
});

export default connect(mapStateToProps)(Settings);